// SENTINEL: 2_Structured - Idea Manager (CRUD, Navigation, Organization)
// Vitality: Healthy (Refactored Pass 7)
import React, { useState, useEffect, useRef, useCallback, useMemo, lazy, Suspense } from 'react';
import { Idea, Section, Attachment, ExecutionPlan } from '../types';
import {
  Plus, Search, X, MoreVertical, Edit2, Trash2,
  MessageSquare, Layout, Sparkles, Folder, File,
  Maximize2, Minimize2, ChevronRight, ChevronDown,
  GripVertical, Paperclip, Check, Upload, Wand2,
  Layers, Brain, Edit3, Cloud, ExternalLink,
  Printer, FileText, Presentation, FileSpreadsheet,
  Loader2, Download, RefreshCw, PanelLeft, PanelLeftClose,
  Palette, ImageIcon, FolderPlus, FileDown, MessageCircle, BookOpen
} from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';

const AIEdgeManager = lazy(() => import('./AIEdgeManager').then(m => ({ default: m.AIEdgeManager })));
import { logger } from '../services/loggingService';
import { ChatInterface } from './ChatInterface';
import { generatePresentationData, generateSpreadsheetData, generateConceptImage } from '../services/geminiService';
import { googleDriveService } from '../services/googleDriveService';
import { ToastType } from './Toast';
import { Carousel3D } from './Carousel3D';
import { useSmoothScroll } from '../hooks';
import { ExportSelectionModal, ExportType } from './ExportSelectionModal';
import { ImportProjectModal } from './ImportProjectModal';
import pptxgen from "pptxgenjs";
import * as XLSX from "xlsx";

interface IdeaManagerProps {
  ideas: Idea[];
  activeIdeaId: string | null;
  onSelectIdea: (id: string | null) => void;
  onCreateIdea: (title: string, content?: string, sections?: Section[], attachments?: Attachment[]) => void;
  onUpdateIdea: (id: string, content: string, sections?: Section[], attachments?: Attachment[], title?: string, googleDocId?: string, googleFolderId?: string) => void;
  onDeleteIdea: (id: string) => void;
  theme: 'light' | 'dark';
  showToast: (title: string, message: string, type: ToastType) => void;
  isCloudMode?: boolean;
  getProjectDrivePath?: (title: string) => string;
  isLiveMode?: boolean;
  showTileEdgeColors?: boolean;
  onEditModeChange?: (isEditing: boolean) => void;
  chatProps?: any;
}

const generateId = () => Math.random().toString(36).substring(2, 9);

const toTitleCase = (str: string): string => {
  return str.replace(/\b\w/g, char => char.toUpperCase());
};

// Custom simple debounce hook to avoid external dependencies
function useDebounce<T extends (...args: any[]) => void>(callback: T, delay: number) {
  const callbackRef = useRef(callback);
  useEffect(() => { callbackRef.current = callback; });
  return useMemo(() => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => callbackRef.current(...args), delay);
    };
  }, [delay]);
}

const SectionBlock = React.memo(({
  section,
  onUpdate,
  theme,
  isActive,
  isWholeDoc
}: {
  section: Section,
  onUpdate: (html: string, id: string) => void,
  theme: 'light' | 'dark',
  isActive: string | null,
  isWholeDoc: boolean
}) => {
  const [localContent, setLocalContent] = useState(section.content);

  // Determine if we should sync from props (only if significantly different to allow external edits/AI)
  // We skip sync if the content is exactly what we just typed (handled by flow)
  // But we MUST sync if the prop changes from outside.
  useEffect(() => {
    if (section.content !== localContent) {
      setLocalContent(section.content);
    }
  }, [section.content]);

  const debouncedUpdate = useDebounce((html: string) => {
    onUpdate(html, section.id);
  }, 600); // 600ms debounce

  const handleChange = useCallback((html: string) => {
    setLocalContent(html);
    debouncedUpdate(html);
  }, [debouncedUpdate]);

  return (
    <div className="mb-12 group/section gpu-accelerated performance-container">
      <div className="mb-4 pb-2 border-b border-cc-border-muted flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`font-bold text-cc-text ${section.parentId ? 'text-lg ml-4' : 'text-2xl'}`}>
            {section.title}
          </span>
          {section.id !== isActive && isWholeDoc && (
            <span className="text-[10px] uppercase font-bold text-cc-text-muted bg-cc-tile px-1.5 py-0.5 rounded">Child Section</span>
          )}
        </div>
      </div>

      <div className="min-h-[100px]">
        <RichTextEditor
          initialContent={localContent}
          onChange={handleChange}
          theme={theme}
          placeholder="Start typing..."
          hideHeadings={true}
        />
      </div>
    </div>
  );
}, (prev, next) => {
  // Memoization check: 
  // Re-render if content changes (external) OR title OR theme OR active state
  // Note: We deliberately ignore checking section.content for EQUALITY in the sense that
  // if the parent has the same content as we do, we don't need to re-render.
  // But if parent has New content, we do.
  return prev.section.content === next.section.content &&
    prev.section.title === next.section.title &&
    prev.theme === next.theme &&
    prev.isActive === next.isActive;
});

export const IdeaManager: React.FC<IdeaManagerProps> = React.memo(({
  ideas,
  activeIdeaId,
  onSelectIdea,
  onCreateIdea,
  onUpdateIdea,
  onDeleteIdea,
  theme,
  showToast,
  isCloudMode = false,
  getProjectDrivePath,
  isLiveMode = false,
  showTileEdgeColors = true,
  onEditModeChange,
  chatProps
}) => {
  console.log('[IdeaManager] Render', { hasChatProps: !!chatProps });
  const [isCreating, setIsCreating] = useState(false);
  const [newTitle, setNewTitle] = useState('');

  // Edit Mode States
  const [editingId, setEditingId] = useState<string | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [renamingSectionId, setRenamingSectionId] = useState<string | null>(null);

  // Temporary State for Edit Mode
  const [tempIdea, setTempIdea] = useState<Idea | null>(null);

  // Drag and Drop State
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);

  // Auto-save state
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved'>('saved');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Export/Generation States
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isGeneratingPPT, setIsGeneratingPPT] = useState(false);
  const [isGeneratingXLS, setIsGeneratingXLS] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [imagePrompt, setImagePrompt] = useState('');
  const [showImagePrompt, setShowImagePrompt] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Chat Panel State
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false); // Default closed for space efficiency

  // Export Selection Modal State
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedExportType, setSelectedExportType] = useState<ExportType | null>(null);

  // Import Modal State
  const [importModalOpen, setImportModalOpen] = useState(false);
  // If editingId is set when opening, we merge. Otherwise new.
  const [isMergeImport, setIsMergeImport] = useState(false);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // View Mode State (carousel vs flat)
  const [viewMode, setViewMode] = useState<'carousel' | 'flat'>('flat');

  // Native scroll is preferred for large lists
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Native scroll for sidebar
  const sidebarScrollRef = useRef<HTMLDivElement>(null);

  // Collapsible Sidebar State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    // Auto-collapse on mobile (< 768px) by default
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });
  const [isHoverPreviewVisible, setIsHoverPreviewVisible] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [chatPanelWidth, setChatPanelWidth] = useState(384); // Default 384px (w-96)
  const isResizingChat = useRef(false);

  const sidebarRef = useRef<HTMLDivElement>(null);
  const chatPanelRef = useRef<HTMLDivElement>(null);

  const startChatResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizingChat.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none'; // Prevent selection

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (isResizingChat.current && chatPanelRef.current) {
        // Calculate new width: window width - mouse X
        const newWidth = window.innerWidth - moveEvent.clientX;
        // Clamp width (min 300, max 800)
        const clampedWidth = Math.max(300, Math.min(800, newWidth));

        // Direct DOM update for performance (no re-renders)
        chatPanelRef.current.style.width = `${clampedWidth}px`;
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isResizingChat.current) {
        isResizingChat.current = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);

        // Sync state at the end
        const newWidth = window.innerWidth - e.clientX;
        setChatPanelWidth(Math.max(300, Math.min(800, newWidth)));
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, []);

  // Collapsed Sections State (Set of IDs)
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  const toggleSectionCollapse = (sectionId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Filter ideas based on search query
  const filteredIdeas = searchQuery.trim()
    ? ideas.filter(idea =>
      idea.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      idea.sections?.some(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
    )
    : ideas;

  // Focus search input when expanded
  useEffect(() => {
    if (isSearchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchExpanded]);

  // Persist view mode preference
  useEffect(() => {
    localStorage.setItem('ideaManagerViewMode', viewMode);
  }, [viewMode]);

  // Notify parent when edit mode changes (for Live Mode to hide controls)
  useEffect(() => {
    if (onEditModeChange) {
      onEditModeChange(!!editingId);
    }
  }, [editingId, onEditModeChange]);

  // Helper to toggle view mode
  const toggleViewMode = () => {
    logger.info('IdeaManager', 'View Mode Toggled', { from: viewMode, to: viewMode === 'carousel' ? 'flat' : 'carousel' });
    setViewMode(prev => prev === 'carousel' ? 'flat' : 'carousel');
  };

  // Handler for carousel to trigger editing
  const handleCarouselEdit = (id: string) => {
    const idea = ideas.find(i => i.id === id);
    if (idea) {
      const syntheticEvent = { stopPropagation: () => { } } as React.MouseEvent;
      startEditing(syntheticEvent, idea);
    }
  };

  // Handle responsive sidebar collapse
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setIsSidebarCollapsed(true);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Ultra-fast hover preview handlers (desktop only)
  const handleSidebarMouseEnter = useCallback(() => {
    if (isSidebarCollapsed && window.innerWidth >= 768) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHoverPreviewVisible(true);
      }, 100); // 100ms delay for ultra-fast preview
    }
  }, [isSidebarCollapsed]);

  const handleSidebarMouseLeave = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHoverPreviewVisible(false);
  }, []);

  // Initialize Edit Mode
  const startEditing = (e: React.MouseEvent, idea: Idea) => {
    e.stopPropagation();

    // Ensure idea has sections structure if migrating from old version
    let initialSections: Section[] = idea.sections && idea.sections.length > 0
      ? idea.sections
      : [{ id: 'main', title: 'General Overview', content: idea.content || '', lastUpdated: Date.now(), order: 0, parentId: null }];

    // Migration: Add missing fields
    initialSections = initialSections.map((s, idx) => ({
      ...s,
      order: s.order ?? idx,
      parentId: s.parentId ?? null
    }));

    const initializedIdea = {
      ...idea,
      sections: initialSections,
      attachments: idea.attachments || []
    };

    setEditingId(idea.id);
    setTempIdea(initializedIdea);
    setActiveSectionId(initialSections[0].id);
    setSaveStatus('saved');

    // SENTINEL: Start Focus Timer
    editStartTimeRef.current = Date.now();
  };

  // Handle Updates
  const updateSectionContent = (html: string, targetSectionId?: string) => {
    const idToUpdate = targetSectionId || activeSectionId;
    if (!tempIdea || !idToUpdate) return;

    setTempIdea(prev => {
      if (!prev) return null;
      return {
        ...prev,
        sections: prev.sections?.map(s => s.id === idToUpdate ? { ...s, content: html, lastUpdated: Date.now() } : s)
      };
    });
    triggerAutoSave();
  };

  const addSection = (parentId: string | null = null) => {
    if (!tempIdea || !tempIdea.sections) return;

    const siblings = tempIdea.sections.filter(s => s.parentId === parentId);
    const maxOrder = siblings.length > 0 ? Math.max(...siblings.map(s => s.order)) : -1;

    const newSection: Section = {
      id: generateId(),
      title: parentId ? "New Subsection" : "New Section",
      content: "",
      lastUpdated: Date.now(),
      parentId: parentId,
      order: maxOrder + 1
    };

    setTempIdea(prev => prev ? { ...prev, sections: [...(prev.sections || []), newSection] } : null);
    setActiveSectionId(newSection.id);
    setRenamingSectionId(newSection.id); // Auto-focus rename on creation
    triggerAutoSave();
  };

  const deleteSection = (e: React.MouseEvent, sectionId: string) => {
    e.stopPropagation();
    if (!tempIdea || !tempIdea.sections || tempIdea.sections.length <= 1) return; // Prevent deleting last section

    // Also delete children
    const toDelete = [sectionId, ...tempIdea.sections.filter(s => s.parentId === sectionId).map(s => s.id)];
    const newSections = tempIdea.sections.filter(s => !toDelete.includes(s.id));

    setTempIdea(prev => prev ? { ...prev, sections: newSections } : null);
    if (activeSectionId === sectionId || toDelete.includes(activeSectionId || '')) {
      setActiveSectionId(newSections[0].id);
    }
    triggerAutoSave();
  };

  const renameSection = (sectionId: string, newTitle: string) => {
    setTempIdea(prev => {
      if (!prev) return null;
      return {
        ...prev,
        sections: prev.sections?.map(s => s.id === sectionId ? { ...s, title: newTitle } : s)
      };
    });
    triggerAutoSave();
  };

  // Handle Idea Deletion
  const handleDelete = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();

    // SENTINEL: Idea Survival Tracking
    const idea = ideas.find(i => i.id === id);
    if (idea) {
      const lifespanSec = Math.round((Date.now() - idea.createdAt) / 1000);
      if (lifespanSec < 60) {
        logger.info('IdeaManager', 'Idea Abandoned (False Start)', { lifespanSec, title: idea.title });
      } else {
        logger.info('IdeaManager', 'Idea Deleted', { lifespanSec, title: idea.title });
      }
    }

    if (confirm('Delete this project? This cannot be undone.')) {
      if (activeIdeaId === id) {
        onSelectIdea(null); // Deselect if the active idea is deleted
      }
      onDeleteIdea(id);
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e: React.DragEvent, id: string) => {
    e.stopPropagation();
    setDraggedSectionId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault(); // Allow drop
  };

  const handleDrop = (e: React.DragEvent, targetId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (!draggedSectionId || draggedSectionId === targetId || !tempIdea?.sections) return;

    const draggedItem = tempIdea.sections.find(s => s.id === draggedSectionId);
    const targetItem = tempIdea.sections.find(s => s.id === targetId);

    if (!draggedItem || !targetItem) return;

    // Rule: Only allow reordering among siblings (same parent)
    if (draggedItem.parentId !== targetItem.parentId) return;

    // Reorder logic
    const siblings = tempIdea.sections.filter(s => s.parentId === draggedItem.parentId).sort((a, b) => a.order - b.order);
    const oldIndex = siblings.findIndex(s => s.id === draggedSectionId);
    const newIndex = siblings.findIndex(s => s.id === targetId);

    // Remove from array and insert at new position
    const reorderedSiblings = [...siblings];
    const [moved] = reorderedSiblings.splice(oldIndex, 1);
    reorderedSiblings.splice(newIndex, 0, moved);

    // Update orders
    const updatedSiblings = reorderedSiblings.map((s, idx) => ({ ...s, order: idx }));

    // Merge back into main sections array
    const otherSections = tempIdea.sections.filter(s => s.parentId !== draggedItem.parentId);

    setTempIdea(prev => prev ? { ...prev, sections: [...otherSections, ...updatedSiblings] } : null);
    setDraggedSectionId(null);
    triggerAutoSave();
  };

  // Attachments
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, sectionId?: string) => {
    if (!e.target.files || !tempIdea) return;
    const file = e.target.files[0];
    e.target.value = '';

    setIsUploadingAttachment(true);

    try {
      if (isCloudMode && tempIdea.googleFolderId) {
        const result = await googleDriveService.uploadAttachment(tempIdea, file, sectionId);
        if (result) {
          const newAttachment: Attachment = {
            id: generateId(),
            name: file.name,
            type: file.type.startsWith('image/') ? 'image' : 'document',
            mimeType: file.type,
            timestamp: Date.now(),
            driveFileId: result.fileId,
            driveWebLink: result.webLink,
            sectionId: sectionId
          };

          setTempIdea(prev => prev ? { ...prev, attachments: [...(prev.attachments || []), newAttachment] } : null);
          triggerAutoSave();
          triggerAutoSave();
          showToast("Attachment Added", file.name + " uploaded to Google Drive", 'success');
          logger.info('IdeaManager', 'Attachment Added (Drive)', { name: file.name, type: file.type });
        } else {
          throw new Error("Upload failed");
        }
      } else {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64 = reader.result as string;
          const newAttachment: Attachment = {
            id: generateId(),
            name: file.name,
            type: file.type.startsWith('image/') ? 'image' : 'document',
            mimeType: file.type,
            data: base64,
            timestamp: Date.now(),
            sectionId: sectionId
          };

          setTempIdea(prev => prev ? { ...prev, attachments: [...(prev.attachments || []), newAttachment] } : null);
          triggerAutoSave();
          triggerAutoSave();
          showToast("Attachment Added", "File saved locally.", 'success');
          logger.info('IdeaManager', 'Attachment Added (Local)', { name: file.name, type: file.type });
          setIsUploadingAttachment(false);
        };
        reader.onerror = () => {
          showToast("Upload Failed", "Could not read file.", 'error');
          setIsUploadingAttachment(false);
        };
        reader.readAsDataURL(file);
        return;
      }
    } catch (err) {
      console.error("Upload error:", err);
      showToast("Upload Failed", "Could not upload file.", 'error');
    }
    setIsUploadingAttachment(false);
  };

  const deleteAttachment = async (attId: string) => {
    const attachment = tempIdea?.attachments?.find(a => a.id === attId);
    if (attachment?.driveFileId && isCloudMode) {
      await googleDriveService.deleteAttachment(attachment.driveFileId);
    }
    setTempIdea(prev => prev ? { ...prev, attachments: prev.attachments?.filter(a => a.id !== attId) } : null);
    triggerAutoSave();
  };

  const getSectionAttachments = (sectionId: string) => {
    return tempIdea?.attachments?.filter(a => a.sectionId === sectionId) || [];
  };

  const getGeneralAttachments = () => {
    return tempIdea?.attachments?.filter(a => !a.sectionId) || [];
  };

  const getAttachmentLink = (att: Attachment) => {
    if (att.driveWebLink) return att.driveWebLink;
    if (att.data) return att.data;
    return '#';
  };

  // Auto-Save Logic
  const triggerAutoSave = () => {
    setSaveStatus('unsaved');
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      if (tempIdea && editingId) {
        setSaveStatus('saving');
        const flattenedContent = tempIdea.sections
          ?.sort((a, b) => a.order - b.order)
          .map(s => "## " + s.title + " \n" + s.content + " ")
          .join('\n\n') || '';

        onUpdateIdea(
          editingId,
          flattenedContent,
          tempIdea.sections,
          tempIdea.attachments,
          tempIdea.title
        );

        // RAG Ingest (Fire and forget)
        import('../services/ragService').then(({ ragService }) => {
          ragService.ingestProject(tempIdea);
        }).catch(err => console.error(err));

        setTimeout(() => setSaveStatus('saved'), 500);
        logger.debug('IdeaManager', 'AutoSave Triggered', { ideaId: editingId, sectionCount: tempIdea.sections?.length });
      }
    }, 1000);
  };

  const handleCreate = () => {
    if (newTitle.trim()) {
      onCreateIdea(toTitleCase(newTitle.trim()));
      logger.info('IdeaManager', 'Idea Created', { title: newTitle.trim() });
      setNewTitle('');
      setIsCreating(false);
    }
  };

  const handleImport = (parsedData: { title: string, content: string, sections: Section[], attachments: Attachment[] }) => {
    if (editingId && tempIdea) {
      // Merge Mode

      // 1. Shift order of imported sections to be after existing ones
      const maxOrder = tempIdea.sections?.reduce((max, s) => Math.max(max, s.order), -1) || -1;
      const shiftedSections = parsedData.sections.map((s, i) => ({
        ...s,
        order: maxOrder + 1 + i,
        parentId: s.parentId || null // Keep hierarchy from import?
        // Issue: Imported IDs are random. Parent references within import are consistent.
        // But if we append to root, top level imports have null parent.
      }));

      // 2. Append Sections
      const newSections = [...(tempIdea.sections || []), ...shiftedSections];

      // 3. Append Attachments
      const newAttachments = [...(tempIdea.attachments || []), ...parsedData.attachments];

      // 4. Update Idea (preserve title unless empty?)
      onUpdateIdea(editingId, tempIdea.content, newSections, newAttachments, tempIdea.title);
      // Update local temp state
      setTempIdea(prev => prev ? { ...prev, sections: newSections, attachments: newAttachments } : null);

      showToast("Import Successful", "Merged " + parsedData.sections.length + " sections.", 'success');
      logger.info('IdeaManager', 'Project Imported (Merge)', { title: parsedData.title, sections: parsedData.sections.length });

    } else {
      // Create New Mode
      onCreateIdea(parsedData.title || "Imported Project", parsedData.content, parsedData.sections, parsedData.attachments);

      // We can't immediately set sections because onCreate is async-ish/detached in App.tsx
      // But actually onCreate just adds to state.
      // We might need a way to set content immediately.
      // The current prop `onCreateIdea` only takes title.
      // Hack: Wait for it to be created/selected then update?
      // Improve `onCreateIdea` to accept data? App.tsx handleCreateIdea accepts `initialContent`.
      // But sections structure is complex.

      // Let's modify App.tsx later to support full object creation?
      // Or just create then update.
      // Since we don't have the ID of the new idea here easily without refactoring props...

      // Wait! App.tsx `handleCreateIdea` logic:
      // setIdeas([...ideas, newIdea]); setActiveIdeaId(newIdea.id);

      // We can't easily hook into that from here.
      // FOR NOW: Let's assume the user just created it.
      // Implementation detail: Use a special "pending import" state?
      // Or update `onCreateIdea` in types.ts?

      // Actually, `activeIdeaId` changes to the new idea.
      // We could listen for `activeIdeaId` change and if `pendingImport` exists, apply it.
    }
  };

  // Refined Create Handlers to support Import Injection
  // We need to lift this logic to App.tsx OR change the interface.
  // Let's disable "New Project Import" for a second and check `IdeaManagerProps`.
  // `onCreateIdea: (title: string) => void; `
  // We should duplicate the parsing/creation logic in App.tsx OR pass full object.
  // Let's modify `onCreateIdea` signature or use a different prop.
  // Actually, for this task, I'll implement the UI here but `handleImport` for NEW projects might need
  // to be passed up.
  // Let's stick to "Merge" first as per request "import ... into existing idea".
  // "Import document - for creating an idea... Or importing information into..."

  // Let's add `onImportIdea ?: (idea: Idea) => void ` prop? Or just pass parsed data up.
  // Actually, I can use `handleCreate` then immediately `onUpdate` if I knew the ID.
  // But I don't know the ID generated by App.tsx.

  // Strategy: Update `onCreateIdea` to optionally accept initial data?
  // I will just use `showToast` for now effectively saying "Select a project to import into"
  // OR Implement "Create New" by passing a signal.

  // Actually, I can allow the user to Create blank -> Then Import.
  // BUT the modal acts as "Create Project".

  // Let's enable Merge (In-Project) first.

  const handleImportClick = () => {
    setImportModalOpen(true);
  };

  // --- EXPORT HANDLERS ---

  const openExportModal = (type: ExportType) => {
    setSelectedExportType(type);
    setExportModalOpen(true);
    setIsExportMenuOpen(false);
  };

  const handleExportWithSections = (selectedSections: Section[]) => {
    if (!selectedExportType || !tempIdea) return;

    switch (selectedExportType) {
      case 'word':
        handleDocDownload(selectedSections);
        break;
      case 'pdf':
        handlePdfPrint(selectedSections);
        break;
      case 'pptx':
        handlePptxGeneration(selectedSections);
        break;
      case 'xlsx':
        handleXlsxGeneration(selectedSections);
        break;
    }

    setExportModalOpen(false);
    setSelectedExportType(null);
  };

  const handleDocDownload = (sections: Section[]) => {
    if (!tempIdea) return;
    const sortedSections = [...sections].sort((a, b) => a.order - b.order);
    const sectionIds = new Set(sections.map(s => s.id));
    const timestamp = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const sectionCount = sections.length;

    // Find root-level sections (either no parent, or parent not in selection)
    const rootSections = sortedSections.filter(s => !s.parentId || !sectionIds.has(s.parentId));

    let htmlContent = `
  < html xmlns: o = 'urn:schemas-microsoft-com:office:office' xmlns: w = 'urn:schemas-microsoft-com:office:word' xmlns = 'http://www.w3.org/TR/REC-html40' >
        <head><meta charset="utf-8"><title>${tempIdea.title}</title>
        <style>
          /* Premium Typography Matches Editor */
          @page { margin: 1in; }
          body { font-family: 'Calibri', 'Arial', sans-serif; font-size: 11pt; line-height: 1.6; color: #1e293b; }

          /* Headings */
          h1 { font-size: 24pt; font-weight: 800; color: #0f172a; margin-top: 24pt; margin-bottom: 12pt; border-bottom: 3px solid #3b82f6; padding-bottom: 8pt; letter-spacing: -0.02em; }
          h2 { font-size: 16pt; font-weight: 700; color: #0f172a; margin-top: 18pt; margin-bottom: 8pt; }
          h3 { font-size: 13pt; font-weight: 600; color: #3b82f6; margin-top: 14pt; margin-bottom: 6pt; }

          /* Content */
          p { margin-bottom: 10pt; text-align: justify; }
          ul, ol { margin-left: 20pt; margin-bottom: 12pt; }
          li { margin-bottom: 4pt; }

          /* Premium Styles */
          blockquote {
            border-left: 4px solid #3b82f6;
            background-color: #f8fafc;
            padding: 12pt 16pt;
            margin: 16pt 0;
            font-style: italic;
            color: #475569;
            border-radius: 0 8pt 8pt 0;
          }

          a { color: #3b82f6; text-decoration: none; font-weight: 500; }

          img {
            max-width: 100%;
            height: auto;
            border-radius: 8pt;
            margin: 12pt auto;
            display: block;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          }

          code {
            font-family: 'Courier New', monospace;
            background-color: #e2e8f0;
            padding: 2pt 4pt;
            border-radius: 3pt;
            font-size: 0.9em;
            color: #2563eb;
          }

          pre {
            background-color: #f1f5f9;
            border: 1px solid #e2e8f0;
            padding: 12pt;
            border-radius: 6pt;
            font-family: 'Courier New', monospace;
            font-size: 9pt;
            white-space: pre-wrap;
          }

          /* Structure */
          .header-info { font-size: 9pt; color: #64748b; margin-bottom: 24pt; border-bottom: 1px solid #e2e8f0; padding-bottom: 12pt; }
          .footer { font-size: 8pt; color: #94a3b8; margin-top: 36pt; border-top: 1px solid #e2e8f0; padding-top: 12pt; text-align: center; }
          .toc { background: #f8fafc; padding: 16pt; margin-bottom: 24pt; border-radius: 4pt; border: 1px solid #e2e8f0; }
          .toc-title { font-size: 14pt; font-weight: 600; color: #1e40af; margin-bottom: 12pt; }
          .toc-item { font-size: 10pt; color: #475569; margin-bottom: 4pt; }
        </style>
        </head><body>
        <h1>${tempIdea.title}</h1>
        <div class="header-info">
          <strong>Generated:</strong> ${timestamp} | <strong>Sections:</strong> ${sectionCount} | <strong>Source:</strong> ConceptCanvas AI
        </div>
        <div class="toc">
          <div class="toc-title">Table of Contents</div>
          ${rootSections.map((s, i) => `<div class="toc-item">${i + 1}. ${s.title}</div>`).join('')}
        </div>`;

    const renderSectionRecursive = (parentId: string | null, depth: number, isOrphanRoot: boolean = false) => {
      // For orphan roots, find sections that have this as parent AND are in selection
      // For normal recursion, find children that are in selection
      const children = sortedSections.filter(s => {
        if (isOrphanRoot) {
          return s.id === parentId; // Render the orphan itself
        }
        return s.parentId === (parentId || null) && sectionIds.has(s.id);
      });

      for (const section of children) {
        const Tag = depth === 0 ? 'h2' : 'h3';
        htmlContent += `<${Tag}>${section.title}</${Tag}>${section.content}`;
        // Recurse into children that are in the selection
        const selectedChildren = sortedSections.filter(s => s.parentId === section.id);
        if (selectedChildren.length > 0) {
          for (const child of selectedChildren) {
            const ChildTag = 'h3';
            htmlContent += `<${ChildTag}>${child.title}</${ChildTag}>${child.content}`;
          }
        }
      }
    };

    // Render each root section (including orphans)
    for (const section of rootSections) {
      htmlContent += `<h2>${section.title}</h2>${section.content}`;
      // Render any selected children of this section
      const selectedChildren = sortedSections.filter(s => s.parentId === section.id);
      for (const child of selectedChildren) {
        htmlContent += `<h3>${child.title}</h3>${child.content}`;
      }
    }

    htmlContent += `<div class="footer">Document generated by ConceptCanvas AI on ${timestamp}</div></body></html > `;
    const blob = new Blob(['\ufeff', htmlContent], { type: 'application/msword' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${tempIdea.title.replace(/[^a-z0-9]/gi, '_')}.doc`;
    link.click();
    showToast("Export Complete", "Word document ready.", 'success');
  };

  const handlePdfPrint = (sections: Section[]) => {
    if (!tempIdea) return;
    const sortedSections = [...sections].sort((a, b) => a.order - b.order);
    const sectionIds = new Set(sections.map(s => s.id));
    const timestamp = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    // Find root-level sections (either no parent, or parent not in selection)
    const rootSections = sortedSections.filter(s => !s.parentId || !sectionIds.has(s.parentId));

    const renderSectionsHtml = () => {
      let html = '';
      // Render each root section (including orphans)
      for (const section of rootSections) {
        html += `< h2 > ${section.title}</h2 > ${section.content} `;
        // Render any selected children of this section
        const selectedChildren = sortedSections.filter(s => s.parentId === section.id);
        for (const child of selectedChildren) {
          html += `< h3 > ${child.title}</h3 > ${child.content} `;
        }
      }
      return html;
    };

    // Create or get the hidden print container
    let printContainer = document.getElementById('cc-print-container');
    if (!printContainer) {
      printContainer = document.createElement('div');
      printContainer.id = 'cc-print-container';
      document.body.appendChild(printContainer);
    }

    // Inject filtered content
    printContainer.innerHTML = `
  <style>
@media screen { #cc - print - container { display: none!important; } }
@media print {
  body > *: not(#cc - print - container) { display: none!important; }
  #cc - print - container {
    display: block!important;
    position: absolute!important;
    left: 0!important;
    top: 0!important;
    width: 100 % !important;
  }
  @page { margin: 1in; }
          body { -webkit - print - color - adjust: exact; print - color - adjust: exact; }
}
#cc - print - container { font - family: 'Georgia', 'Times New Roman', serif; font - size: 11pt; line - height: 1.7; color: #1e293b; max - width: 800px; margin: 0 auto; padding: 40px; }
#cc - print - container h1 { font - size: 28pt; color: #1e40af; border - bottom: 3px solid #3b82f6; padding - bottom: 12pt; margin - bottom: 24pt; font - weight: 700; }
#cc - print - container h2 { font - size: 16pt; color: #1e3a8a; margin - top: 32pt; margin - bottom: 12pt; border - left: 4px solid #3b82f6; padding - left: 12pt; font - weight: 600; }
#cc - print - container h3 { font - size: 13pt; color: #475569; margin - top: 20pt; margin - bottom: 8pt; font - weight: 600; }
#cc - print - container p { margin - bottom: 12pt; text - align: justify; }
#cc - print - container ul, #cc - print - container ol { margin - left: 24pt; margin - bottom: 12pt; }
#cc - print - container li { margin - bottom: 6pt; }
#cc - print - container.header - info { font - size: 10pt; color: #64748b; margin - bottom: 24pt; border - bottom: 1px solid #e2e8f0; padding - bottom: 12pt; }
#cc - print - container.footer { font - size: 9pt; color: #94a3b8; margin - top: 48pt; border - top: 1px solid #e2e8f0; padding - top: 12pt; text - align: center; }
#cc - print - container.toc { background: #f8fafc; padding: 20pt; margin - bottom: 32pt; border - radius: 4pt; page -break-after: always; }
#cc - print - container.toc - title { font - size: 14pt; font - weight: 700; color: #1e40af; margin - bottom: 12pt; }
#cc - print - container.toc - item { font - size: 10pt; color: #475569; margin - bottom: 6pt; }
      </style >
      <h1>${tempIdea.title}</h1>
      <div class="header-info">
        <strong>Generated:</strong> ${timestamp} | <strong>Sections:</strong> ${sections.length} | <strong>Source:</strong> ConceptCanvas AI
      </div>
      <div class="toc">
        <div class="toc-title">Table of Contents</div>
        ${rootSections.map((s, i) => `<div class="toc-item">${i + 1}. ${s.title}</div>`).join('')}
      </div>
      ${renderSectionsHtml()}
<div class="footer">Document generated by ConceptCanvas AI on ${timestamp}</div>
`;

    // Trigger print dialog
    setTimeout(() => {
      window.print();
      showToast('Print Ready', 'Print dialog opened.', 'success');
    }, 100);
  };

  const handlePptxGeneration = async (sections: Section[]) => {
    if (!tempIdea) return;
    setIsGeneratingPPT(true);
    try {
      // Create a temporary idea with only selected sections
      const filteredIdea = { ...tempIdea, sections };
      const data = await generatePresentationData(filteredIdea);
      const pres = new pptxgen();
      pres.layout = 'LAYOUT_16x9';
      pres.author = 'ConceptCanvas AI';
      pres.title = tempIdea.title;
      pres.subject = 'AI-Generated Presentation';

      const BRAND_BLUE = '1e40af';
      const BRAND_LIGHT = '3b82f6';
      const TEXT_DARK = '1e293b';
      const TEXT_MUTED = '64748b';

      const titleSlide = pres.addSlide();
      titleSlide.addShape('rect', { x: 0, y: 0, w: '100%', h: '100%', fill: { color: 'f8fafc' } });
      titleSlide.addShape('rect', { x: 0, y: 4.5, w: '100%', h: 0.8, fill: { color: BRAND_BLUE } });
      titleSlide.addText(tempIdea.title, { x: 0.5, y: 2, w: '90%', fontSize: 44, bold: true, align: 'center', color: TEXT_DARK });
      titleSlide.addText(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        { x: 0.5, y: 3.2, w: '90%', fontSize: 16, align: 'center', color: TEXT_MUTED });
      titleSlide.addText('Prepared with ConceptCanvas AI', { x: 0.5, y: 4.65, w: '90%', fontSize: 12, align: 'center', color: 'ffffff' });

      data.slides.forEach((slide: any, idx: number) => {
        const s = pres.addSlide();
        s.addShape('rect', { x: 0, y: 0, w: '100%', h: 0.8, fill: { color: BRAND_BLUE } });
        s.addText(slide.title, { x: 0.5, y: 0.15, w: '85%', fontSize: 22, bold: true, color: 'ffffff' });
        s.addText(`${idx + 1} `, { x: 9, y: 0.15, w: 0.5, fontSize: 14, align: 'right', color: 'ffffff' });

        if (slide.bullets && Array.isArray(slide.bullets)) {
          const bulletItems = slide.bullets.map((b: string) => ({
            text: b,
            options: { fontSize: 16, bullet: { type: 'bullet', color: BRAND_LIGHT }, color: TEXT_DARK, breakLine: true, paraSpaceAfter: 8 }
          }));
          s.addText(bulletItems, { x: 0.5, y: 1.2, w: '90%', h: 4, valign: 'top' });
        }
        if (slide.speakerNotes) s.addNotes(slide.speakerNotes);
      });

      const endSlide = pres.addSlide();
      endSlide.addShape('rect', { x: 0, y: 0, w: '100%', h: '100%', fill: { color: BRAND_BLUE } });
      endSlide.addText('Thank You', { x: 0.5, y: 2.2, w: '90%', fontSize: 48, bold: true, align: 'center', color: 'ffffff' });
      endSlide.addText('Questions & Discussion', { x: 0.5, y: 3.2, w: '90%', fontSize: 20, align: 'center', color: 'e0e7ff' });

      pres.writeFile({ fileName: `${tempIdea.title} _Presentation.pptx` });
      showToast("Export Complete", "PowerPoint presentation generated.", 'success');
    } catch (e) {
      console.error("PPT Generation failed", e);
      showToast("Export Failed", "Could not generate presentation.", 'error');
    } finally {
      setIsGeneratingPPT(false);
    }
  };

  const handleXlsxGeneration = async (sections: Section[]) => {
    if (!tempIdea) return;
    setIsGeneratingXLS(true);
    try {
      // Create a temporary idea with only selected sections
      const filteredIdea = { ...tempIdea, sections };
      const data = await generateSpreadsheetData(filteredIdea);
      const wb = XLSX.utils.book_new();

      data.sheets.forEach((sheet: any) => {
        const wsData = [sheet.headers, ...sheet.rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        XLSX.utils.book_append_sheet(wb, ws, sheet.name.substring(0, 31)); // Max 31 chars
      });

      if (data.sheets.length === 0) {
        const ws = XLSX.utils.aoa_to_sheet([["No structured data found"]]);
        XLSX.utils.book_append_sheet(wb, ws, "Info");
      }

      XLSX.writeFile(wb, `${tempIdea.title} _Data.xlsx`);
      showToast("Export Complete", "Excel spreadsheet generated.", 'success');
    } catch (e) {
      console.error("XLS Generation failed", e);
      showToast("Export Failed", "Could not generate spreadsheet.", 'error');
    } finally {
      setIsGeneratingXLS(false);
    }
  };

  const handleGenerateArt = async () => {
    if (!imagePrompt.trim() || !tempIdea) return;
    setIsGeneratingImage(true);
    try {
      const projectContext = {
        title: tempIdea.title,
        sections: tempIdea.sections?.map(s => ({
          title: s.title,
          content: s.content.replace(/<[^>]*>/g, '').substring(0, 200)
        }))
      };
      const b64 = await generateConceptImage(imagePrompt, projectContext);
      if (b64) {
        const newAttachment: Attachment = {
          id: generateId(),
          name: `AI_Concept_${Date.now()}.png`,
          type: 'image',
          mimeType: 'image/png',
          data: b64,
          timestamp: Date.now()
        };
        setTempIdea(prev => prev ? { ...prev, attachments: [...(prev.attachments || []), newAttachment] } : null);
        triggerAutoSave();
        setShowImagePrompt(false);
        setImagePrompt('');
        showToast("Art Generated", "Image added to attachments.", 'success');
      } else {
        showToast("Generation Failed", "Could not create image.", 'error');
      }
    } catch (e) {
      console.error("Image Gen Failed", e);
      showToast("Generation Failed", "An error occurred.", 'error');
    } finally {
      setIsGeneratingImage(false);
    }
  };

  // --- Render Helpers ---

  // Recursive Helper to flatten section tree for rendering
  const getSectionsToRender = useCallback(() => {
    if (!tempIdea?.sections) return [];

    // Recursive traversal function
    const traverse = (parentId: string | null): Section[] => {
      const children = tempIdea.sections!
        .filter(s => s.parentId === parentId)
        .sort((a, b) => a.order - b.order);
      return children.flatMap(child => [child, ...traverse(child.id)]);
    };

    if (activeSectionId === 'WHOLE_DOCUMENT') {
      return traverse(null);
    }

    if (!activeSectionId) return [];

    const activeSection = tempIdea.sections.find(s => s.id === activeSectionId);
    if (!activeSection) return [];

    return [activeSection, ...traverse(activeSectionId)];
  }, [tempIdea, activeSectionId]);

  const renderSectionItem = (section: Section, depth: number = 0) => {
    if (!tempIdea || !tempIdea.sections) return null;

    const children = tempIdea.sections
      .filter(s => s.parentId === section.id)
      .sort((a, b) => a.order - b.order);

    const hasChildren = children.length > 0;
    const isCollapsed = collapsedSections.has(section.id);
    const isRenaming = renamingSectionId === section.id;

    return (
      <React.Fragment key={section.id}>
        <div
          draggable={!isRenaming}
          onDragStart={(e) => handleDragStart(e, section.id)}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, section.id)}
          onClick={() => setActiveSectionId(section.id)}
          className={`group flex items-start justify-between py-1.5 pr-2 pl-2 rounded-lg cursor-pointer transition-all mb-0.5 relative ${activeSectionId === section.id
            ? 'bg-cc-elevated shadow-sm border border-cc-border text-cc-accent font-medium'
            : 'text-cc-text-muted hover:bg-cc-tile-hover'
            } gpu-layer`}
          style={{ marginLeft: `${depth * 18}px` }}
        >
          {depth > 0 && (
            <div
              className="absolute left-[-8px] top-0 bottom-0 w-px bg-cc-border-muted opacity-50"
            />
          )}
          <div className="flex items-center gap-1.5 w-full min-w-0">
            {/* Collapse Toggle or Spacer */}
            <div className="flex-shrink-0 w-4 h-4 flex items-center justify-center">
              {hasChildren ? (
                <button
                  onClick={(e) => toggleSectionCollapse(section.id, e)}
                  className="p-0.5 rounded hover:bg-cc-tile-hover text-cc-text-muted hover:text-cc-accent transition-colors"
                >
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                </button>
              ) : (
                <div className="w-3" />
              )}
            </div>

            {/* Icon */}
            <div className="flex-shrink-0 relative">
              {depth > 0 && (
                <div
                  className="absolute -left-[14px] top-1/2 w-[10px] h-px bg-cc-border-muted"
                />
              )}
              {depth === 0 ? <Folder size={14} className="text-cc-accent/80" /> : <File size={13} className="text-cc-text-muted" />}
            </div>

            {/* Title */}
            {isRenaming ? (
              <textarea
                autoFocus
                rows={1}
                value={section.title}
                autoCapitalize="words"
                onChange={(e) => {
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                  renameSection(section.id, e.target.value);
                }}
                onBlur={() => setRenamingSectionId(null)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); setRenamingSectionId(null); } }}
                onClick={(e) => e.stopPropagation()}
                className="bg-transparent outline-none w-full text-sm resize-none overflow-hidden min-h-[24px] break-words whitespace-normal h-auto leading-relaxed border-b border-blue-500 rounded-none px-0 py-0"
              />
            ) : (
              <div
                className="text-sm leading-relaxed break-words whitespace-pre-wrap w-full pr-2 truncate select-none"
                onDoubleClick={(e) => { e.stopPropagation(); setRenamingSectionId(section.id); }}
                title={section.title}
              >
                {section.title}
              </div>
            )}
          </div>

          {/* Quick Actions (Hover) */}
          <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 gap-0.5 bg-cc-surface/80 backdrop-blur-sm rounded absolute right-2 top-1/2 -translate-y-1/2 shadow-sm border border-cc-border px-1">
            <button onClick={(e) => { e.stopPropagation(); addSection(section.id); }} className="p-1 text-cc-text-muted hover:text-cc-accent" title="Add Subsection"><Plus size={10} /></button>
            <button onClick={(e) => { e.stopPropagation(); setRenamingSectionId(section.id); }} className="p-1 text-cc-text-muted hover:text-cc-accent" title="Rename"><Edit2 size={10} /></button>
            <label className="cursor-pointer p-1 text-cc-text-muted hover:text-cc-accent" title="Attach file" onClick={(e) => e.stopPropagation()}>
              <Paperclip size={10} />
              <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, section.id)} accept="image/*,.pdf,.txt,.doc,.docx" />
            </label>
            {(tempIdea.sections.length > 1) && <button onClick={(e) => deleteSection(e, section.id)} className="p-1 text-cc-text-muted hover:text-red-500" title="Delete"><Trash2 size={10} /></button>}
          </div>

          {/* Attachment Indicator */}
          {getSectionAttachments(section.id).length > 0 && (
            <div className={`absolute right-1 top-1 w-3 h-3 rounded-full bg-cc-accent text-cc-accent-text text-[8px] font-bold flex items-center justify-center ${activeSectionId === section.id ? 'opacity-0' : 'opacity-100'}`}>
              {getSectionAttachments(section.id).length}
            </div>
          )}
        </div>

        {/* Render Children (if not collapsed) */}
        {!isCollapsed && hasChildren && (
          <div className="relative">
            {/* Vertical Guide Line */}
            <div
              className="absolute left-0 top-0 bottom-0 w-px bg-cc-border-muted"
              style={{ left: `${(depth * 14) + 20}px` }}
            />
            {children.map(child => renderSectionItem(child, depth + 1))}
          </div>
        )}
      </React.Fragment>
    );
  };

  // Render Component
  return (
    <div className={`w-full bg-cc-surface shadow-sm flex flex-col relative transition-colors duration-300 ${editingId ? 'z-[110]' : 'z-20'} `}>

      {/* --- DASHBOARD VIEW (IDEA TILES) --- */}
      {!editingId && (
        <>
          {viewMode === 'carousel' ? (
            <Carousel3D
              ideas={filteredIdeas}
              activeIdeaId={activeIdeaId}
              onSelectIdea={onSelectIdea}
              onCreateIdea={() => setIsCreating(true)}
              onEditIdea={handleCarouselEdit}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              isSearchExpanded={isSearchExpanded}
              onSearchToggle={setIsSearchExpanded}
              showTileEdgeColors={showTileEdgeColors}
              onToggleViewMode={toggleViewMode}
            />
          ) : (
            <div
              ref={scrollContainerRef}
              className="flex items-center p-4 gap-4 overflow-x-auto horizontal-scroll-container gpu-layer"
            >
              {/* Create + Search + View Toggle Buttons */}
              <div className="flex-shrink-0 flex items-center gap-2">
                <button
                  onClick={() => setIsCreating(true)}
                  className="flex items-center justify-center w-12 h-20 rounded-xl bg-cc-tile text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile-hover shadow-sm hover:shadow-md transition-all group relative gpu-layer border border-cc-tile-border hover:border-cc-accent/20"
                  title="Create New Project"
                >
                  <Plus size={20} className="group-hover:scale-110 transition-transform" />
                </button>

                {/* Import Button */}
                <button
                  onClick={handleImportClick}
                  className="flex items-center justify-center w-12 h-20 rounded-xl bg-cc-tile text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile-hover shadow-sm hover:shadow-md transition-all group relative gpu-layer border border-cc-tile-border hover:border-cc-accent/20"
                  title="Import Project from Text"
                >
                  <FileDown size={20} className="group-hover:scale-110 transition-transform" />
                </button>

                {/* Search Toggle/Input */}
                <div className={`relative flex items-center justify-center h-20 rounded-xl bg-cc-tile border border-cc-tile-border shadow-sm transition-all duration-300 overflow-hidden ${isSearchExpanded ? 'w-32 px-2' : 'w-12 hover:bg-cc-tile-hover hover:border-cc-accent/20 cursor-pointer'
                  } `}>
                  {isSearchExpanded ? (
                    <>
                      <div className="flex flex-col items-center justify-center w-full h-full gap-1">
                        <Search size={14} className="text-cc-accent flex-shrink-0" />
                        <input
                          ref={searchInputRef}
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search..."
                          className="w-full bg-transparent outline-none text-xs text-cc-text placeholder-cc-text-muted text-center"
                          onBlur={() => { if (!searchQuery) setIsSearchExpanded(false); }}
                          onKeyDown={(e) => { if (e.key === 'Escape') { setSearchQuery(''); setIsSearchExpanded(false); } }}
                        />
                      </div>
                      {searchQuery && (
                        <button onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }} className="absolute top-1 right-1 p-0.5 hover:bg-cc-text/10 rounded z-10">
                          <X size={10} className="text-cc-text-muted" />
                        </button>
                      )}
                    </>
                  ) : (
                    <button onClick={() => setIsSearchExpanded(true)} className="w-full h-full flex items-center justify-center">
                      <Search size={18} className="text-cc-text-muted group-hover:text-cc-accent transition-colors" />
                    </button>
                  )}
                </div>

                {/* View Mode Toggle - Shows Layers icon to switch to 3D carousel */}
                <button
                  onClick={toggleViewMode}
                  className="flex items-center justify-center w-12 h-20 rounded-xl bg-cc-tile text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile-hover shadow-sm hover:shadow-md transition-all group relative gpu-layer border border-cc-tile-border hover:border-cc-accent/20"
                  title="Switch to 3D carousel view"
                >
                  <Layers size={18} className="group-hover:scale-110 transition-transform" />
                </button>
              </div>
              <div
                onClick={() => onSelectIdea(null)}
                className={`flex-shrink-0 cursor-pointer w-44 h-20 p-3 rounded-xl border transition-all flex flex-col justify-between relative overflow-hidden group gpu-layer tile-hover-effect ${activeIdeaId === null
                  ? 'border-cc-accent/30 tile-active-glow bg-cc-accent'
                  : 'border-cc-tile-border bg-cc-tile hover:border-cc-accent/20 hover:shadow-sm'
                  } `}
              >
                {activeIdeaId === null && <div className="absolute inset-0 bg-gradient-to-br from-black/5 to-black/10 z-0" />}
                <div className="flex justify-between items-start z-10 relative">
                  <span className={`font-semibold text-base ${activeIdeaId === null ? 'text-cc-accent-text' : 'text-cc-text'} `}>Quick Notes</span>
                  <Brain size={14} className={`${activeIdeaId === null ? 'text-cc-accent-text' : 'text-cc-text-muted'} `} />
                </div>
                <p className={`text-xs relative z-10 font-medium ${activeIdeaId === null ? 'text-cc-accent-text/80' : 'text-cc-text-muted'} `}>Scratchpad for ideas</p>
              </div>
              {/* No Results Message */}
              {searchQuery && filteredIdeas.length === 0 && (
                <div className="flex-shrink-0 flex items-center gap-2 px-4 text-cc-text-muted text-sm">
                  {/* Logging side-effect for empty search */}
                  {(() => { logger.info('IdeaManager', 'Search Returned Zero Results', { query: searchQuery }); return null; })()}
                  <Search size={16} className="opacity-50" />
                  <span>No projects matching "{searchQuery}"</span>
                </div>
              )}
              {filteredIdeas.map((idea, idx) => {
                const gradientColors = [
                  { from: 'rgba(139, 92, 246, 0.9)', to: 'rgba(139, 92, 246, 0)' },  // violet
                  { from: 'rgba(6, 182, 212, 0.9)', to: 'rgba(6, 182, 212, 0)' },    // cyan
                  { from: 'rgba(16, 185, 129, 0.9)', to: 'rgba(16, 185, 129, 0)' },  // emerald
                  { from: 'rgba(245, 158, 11, 0.9)', to: 'rgba(245, 158, 11, 0)' },  // amber
                  { from: 'rgba(236, 72, 153, 0.9)', to: 'rgba(236, 72, 153, 0)' },    // pink
                  { from: 'rgba(59, 130, 246, 0.9)', to: 'rgba(59, 130, 246, 0)' }     // blue
                ];
                const gradient = gradientColors[idx % gradientColors.length];
                const isActive = activeIdeaId === idea.id;
                return (
                  <div
                    key={idea.id}
                    onClick={() => {
                      // SENTINEL: Teleological Tracking (Search Success)
                      if (searchQuery.trim().length > 0) {
                        logger.info('IdeaManager', 'Search Result Selected', { query: searchQuery, ideaId: idea.id });
                      }
                      onSelectIdea(idea);
                    }}
                    className={`
relative flex-shrink-0 cursor-pointer w-52 h-20 rounded-xl transition-all duration-200 group gpu-layer tile-hover-effect overflow-hidden ${isActive
                        ? 'tile-active-glow bg-cc-tile border border-cc-accent/20'
                        : 'bg-cc-tile border border-cc-tile-border hover:border-cc-accent/20 hover:shadow-md'
                      } `}
                  >
                    {showTileEdgeColors && (
                      <div
                        className="absolute left-0 top-0 bottom-0 w-6 pointer-events-none z-0 transition-opacity duration-300"
                        style={{
                          background: `linear-gradient(to right, ${gradient.from}, ${gradient.to})`,
                          borderTopLeftRadius: 'inherit',
                          borderBottomLeftRadius: 'inherit',
                        }}
                      />
                    )}
                    <div className="h-full w-full p-3 flex flex-col justify-between relative z-10">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0 pr-6 max-h-[2.75rem] overflow-y-auto custom-scrollbar-thin scroll-smooth">
                          <span className="font-semibold text-base text-cc-text leading-snug" title={idea.title}>{idea.title}</span>
                        </div>
                        <div className="absolute top-2 right-2 flex items-center gap-1 z-20">
                          {isCloudMode && idea.googleFolderId && (
                            <span className="p-1 rounded-md bg-cc-accent/10" title="Synced to Google Drive">
                              <Cloud size={10} className="text-cc-accent" />
                            </span>
                          )}
                          {isCloudMode && idea.googleDocId && (
                            <span className="p-1 rounded-md bg-cc-accent/10" title="Linked to Google Doc">
                              <Brain size={10} className="text-cc-accent" />
                            </span>
                          )}
                          <button onClick={(e) => startEditing(e, idea)} className="opacity-0 group-hover:opacity-100 p-1 rounded-md bg-cc-accent/90 hover:bg-cc-accent text-white shadow-md transition-all hover:scale-110" title="Open Project">
                            <Edit3 size={12} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 mt-auto">
                        {idea.attachments && idea.attachments.length > 0 && <span className="text-[9px] px-1.5 py-0.5 rounded-md bg-cc-text/5 text-cc-text-muted flex items-center gap-0.5"><Paperclip size={7} /> {idea.attachments.length}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {isCreating && (
        <>
          <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm gpu-layer" onClick={() => setIsCreating(false)} />
          <div className="absolute top-24 left-4 z-50 bg-cc-elevated p-4 rounded-xl shadow-2xl border border-cc-border w-72 animate-in fade-in zoom-in duration-200 gpu-layer">
            <div className="flex items-center gap-2 mb-3"><Sparkles size={16} className="text-cc-accent" /><h3 className="text-sm font-bold text-cc-text">New Project</h3></div>
            <input autoFocus type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} autoCapitalize="words" className="w-full bg-cc-surface border border-cc-border rounded-lg p-2.5 text-sm mb-3 focus:ring-2 focus:ring-cc-accent outline-none text-cc-text" placeholder="e.g., Quantum Garden Project" onKeyDown={(e) => e.key === 'Enter' && handleCreate()} />
            <div className="flex justify-end gap-2"><button onClick={() => setIsCreating(false)} className="px-3 py-1.5 text-xs font-medium text-cc-text-muted hover:bg-cc-tile-hover rounded-lg">Cancel</button><button onClick={handleCreate} className="px-3 py-1.5 text-xs font-medium bg-cc-accent text-cc-accent-text rounded-lg hover:bg-cc-accent-hover shadow-sm">Create</button></div>
          </div>
        </>
      )}

      {/* --- WORKSPACE EDIT VIEW --- */}
      {editingId && tempIdea && (
        <div className="workspace-content fixed inset-x-0 bottom-0 top-9 z-[100] bg-cc-base flex flex-col animate-in fade-in slide-in-from-bottom-5 duration-300 shadow-2xl print-hide gpu-layer">

          {/* Header */}
          <div className="h-14 border-b border-cc-border flex items-center justify-between px-4 md:px-6 bg-cc-elevated flex-shrink-0 print-hide header-bar">
            <div className="flex items-center gap-3 flex-1 min-w-0 mr-4">
              <div className="p-1.5 bg-cc-accent rounded-lg flex-shrink-0 hidden sm:block">
                <Brain size={16} className="text-cc-accent-text" />
              </div>
              <div className="flex-1 min-w-0">
                <input
                  value={tempIdea.title}
                  onChange={(e) => { setTempIdea({ ...tempIdea, title: toTitleCase(e.target.value) }); triggerAutoSave(); }}
                  autoCapitalize="words"
                  className="text-base font-semibold bg-transparent outline-none text-cc-text placeholder-cc-text-muted w-full"
                />
                <div className="flex items-center gap-2 text-[11px] text-cc-text-muted">
                  {(() => {
                    const currentIdea = ideas.find(i => i.id === editingId);
                    const folderId = currentIdea?.googleFolderId || tempIdea.googleFolderId;
                    return isCloudMode && getProjectDrivePath ? (
                      <a
                        href={folderId ? `https://drive.google.com/drive/folders/${folderId}` : '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-1 text-cc-accent hover:opacity-80 transition-opacity ${folderId ? 'hover:underline' : 'cursor-default'}`}
                        title={folderId ? "Open in Google Drive" : getProjectDrivePath(tempIdea.title)}
                        onClick={(e) => { if (!folderId) e.preventDefault(); }}
                      >
                        <Cloud size={10} />
                        <span className="truncate max-w-[180px] hidden sm:inline">{getProjectDrivePath(tempIdea.title)}</span>
                        {folderId && <ExternalLink size={9} className="ml-0.5" />}
                      </a >
                    ) : null;
                  })()}
                </div >
              </div >
            </div >

            {/* Right Actions */}
            < div className="flex items-center gap-2 flex-shrink-0" >

              {/* Import */}
              < button
                onClick={handleImportClick}
                className="px-2.5 py-1.5 rounded-lg text-cc-text-muted hover:text-cc-text hover:bg-cc-tile-hover transition-all text-[11px] font-semibold flex items-center gap-1.5"
                title="Import / Merge Sections"
              >
                <FileDown size={14} /> <span className="hidden sm:inline">Import</span>
              </button >

              {/* Export Suite */}
              < div className="relative" >
                <button
                  onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                  className={`px-2.5 py-1.5 rounded-lg transition-all text-[11px] font-semibold flex items-center gap-1.5 ${isExportMenuOpen ? 'bg-cc-accent/10 text-cc-accent' : 'text-cc-text-muted hover:text-cc-text hover:bg-cc-tile-hover'}`}
                >
                  <Download size={14} /> <span className="hidden sm:inline">Export</span> <ChevronDown size={12} />
                </button>

                {
                  isExportMenuOpen && (
                    <div className="absolute top-full right-0 mt-2 w-56 bg-cc-tile rounded-xl shadow-xl border border-cc-tile-border p-1.5 z-[60] animate-in fade-in zoom-in-95 gpu-layer">
                      <h4 className="px-2.5 py-1.5 text-[9px] font-semibold uppercase text-cc-text-muted tracking-wider">Export Suite</h4>

                      <button onClick={() => { logger.info('Export', 'Export Initiated', { format: 'word' }); openExportModal('word'); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-cc-tile-hover transition-colors text-left text-xs text-cc-text">
                        <FileText size={14} className="text-cc-accent" /> <div><p className="font-medium">Word Document</p><p className="text-[9px] text-cc-text-muted">Formatted text</p></div>
                      </button>

                      <button onClick={() => { logger.info('Export', 'Export Initiated', { format: 'pdf' }); openExportModal('pdf'); }} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-cc-tile-hover transition-colors text-left text-xs text-cc-text">
                        <Printer size={14} className="text-cc-text-muted" /> <div><p className="font-medium">PDF / Print</p><p className="text-[9px] text-cc-text-muted">High-fidelity layout</p></div>
                      </button>

                      <div className="h-px bg-cc-tile-border my-1" />

                      <button onClick={() => openExportModal('pptx')} disabled={isGeneratingPPT} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-cc-tile-hover transition-colors text-left text-xs text-cc-text disabled:opacity-50">
                        {isGeneratingPPT ? <Loader2 size={14} className="animate-spin text-orange-500" /> : <Presentation size={14} className="text-orange-500" />}
                        <div><p className="font-medium">PowerPoint</p><p className="text-[9px] text-cc-text-muted">AI Slides</p></div>
                      </button>

                      <button onClick={() => openExportModal('xlsx')} disabled={isGeneratingXLS} className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-cc-tile-hover transition-colors text-left text-xs text-cc-text disabled:opacity-50">
                        {isGeneratingXLS ? <Loader2 size={14} className="animate-spin text-emerald-500" /> : <FileSpreadsheet size={14} className="text-emerald-500" />}
                        <div><p className="font-medium">Excel</p><p className="text-[9px] text-cc-text-muted">Data Tables</p></div>
                      </button>
                    </div>
                  )
                }
              </div >

              {isCloudMode && (
                <>
                  <button
                    onClick={async () => {
                      if (!tempIdea || !editingId) return;
                      setIsSyncing(true);
                      showToast('Syncing', 'Syncing project to Google Drive...', 'info');
                      try {
                        const result = await googleDriveService.syncIdeaToDoc(tempIdea);
                        if (result && result.folderId) {
                          const updatedIdea = {
                            ...tempIdea,
                            googleDocId: result.docId,
                            googleFolderId: result.folderId,
                            updatedAt: Date.now()
                          };
                          setTempIdea(updatedIdea);
                          const combinedContent = updatedIdea.sections?.map(s => s.content).join(' ') || '';
                          onUpdateIdea(editingId, combinedContent, updatedIdea.sections, updatedIdea.attachments, updatedIdea.title, result.docId, result.folderId);
                          setSaveStatus('saved');
                          showToast('Sync Complete', `Project synced! Folder created in Google Drive.`, 'success');
                        } else {
                          showToast('Sync Issue', 'Sync completed but no folder was created. Check console for details.', 'error');
                        }
                      } catch (err) {
                        console.error('[Sync Error]', err);
                        showToast('Sync Failed', 'Failed to sync to Google Drive. Check console for details.', 'error');
                      } finally {
                        setIsSyncing(false);
                      }
                    }}
                    disabled={isSyncing}
                    className="px-2.5 py-1.5 text-cc-accent hover:bg-cc-accent-muted rounded-lg text-[11px] font-semibold flex items-center gap-1.5 transition-colors disabled:opacity-50"
                    title="Sync to Google Drive"
                  >
                    {isSyncing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                    <span className="hidden sm:inline">Sync</span>
                  </button>
                  <div className="h-5 w-px bg-cc-border" />
                </>
              )}
              <button onClick={() => { if (confirm("Are you sure?")) { onDeleteIdea(editingId); setEditingId(null); } }} className="p-1.5 text-red-500/70 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors"><Trash2 size={14} /></button>
              <div className="h-5 w-px bg-cc-border" />
              <div className="h-5 w-px bg-cc-border" />

              {/* Chat Toggle */}
              {chatProps && (
                <>
                  <button
                    onClick={() => setIsChatPanelOpen(!isChatPanelOpen)}
                    className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold flex items-center gap-1.5 transition-colors ${isChatPanelOpen
                      ? 'bg-cc-accent text-cc-accent-text hover:bg-cc-accent-hover'
                      : 'text-cc-text-muted hover:text-cc-text hover:bg-cc-tile-hover'
                      }`}
                    title="Toggle Chat Assistant"
                  >
                    <MessageCircle size={14} />
                    <span className="hidden sm:inline">{isChatPanelOpen ? 'Hide Chat' : 'Chat'}</span>
                  </button>
                  <div className="h-5 w-px bg-cc-border" />
                </>
              )}

              <button onClick={() => {
                triggerAutoSave();
                setEditingId(null);
                // SENTINEL: Log Focus Duration
                if (editStartTimeRef.current) {
                  const durationSec = Math.round((Date.now() - editStartTimeRef.current) / 1000);
                  const sessionType = durationSec < 60 ? 'Quick Fix' : durationSec > 600 ? 'Deep Work' : 'Standard Session';
                  logger.info('IdeaManager', 'Edit Session Completed', { durationSec, sessionType, ideaId: editingId });
                  editStartTimeRef.current = null;
                }
              }} className="px-3 py-1.5 bg-cc-accent text-cc-accent-text rounded-lg text-[11px] font-semibold hover:bg-cc-accent-hover flex items-center gap-1.5 transition-colors"><Check size={14} /> Done</button>

              <div className="h-5 w-px bg-cc-border" />

              {/* ONNX Badge in Workspace */}
              <div className="scale-90 origin-left">
                <Suspense fallback={<div className="text-[10px] text-red-500 animate-pulse px-2">Loading...</div>}>
                  <AIEdgeManager />
                </Suspense>
              </div>
            </div >
          </div >

          {/* Main Layout */}
          < div className="flex-1 flex overflow-hidden relative" >

            {/* Sidebar Collapse Toggle Button (fixed to left edge) */}
            < button
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              className={`absolute left-0 top-4 z-30 p-1.5 bg-cc-elevated border border-cc-border rounded-r-lg shadow-sm hover:bg-cc-tile-hover transition-all ${isSidebarCollapsed ? 'translate-x-0' : 'translate-x-[calc(20rem-2px)] md:translate-x-[calc(20rem-2px)]'
                }`}
              title={isSidebarCollapsed ? 'Expand Organizer' : 'Collapse Organizer'}
            >
              {isSidebarCollapsed ? <PanelLeft size={14} className="text-cc-text-muted" /> : <PanelLeftClose size={14} className="text-cc-text-muted" />}
            </button >

            {/* Left Sidebar: Sections & Attachments */}
            < div
              ref={sidebarRef}
              onMouseEnter={handleSidebarMouseEnter}
              onMouseLeave={handleSidebarMouseLeave}
              className={`border-r border-cc-border bg-cc-surface flex flex-col flex-shrink-0 sidebar-container transition-all duration-200 ease-out ${isSidebarCollapsed
                ? 'w-0 overflow-hidden opacity-0'
                : 'w-80'
                }`}
            >

              {/* Sections */}
              < div ref={sidebarScrollRef} className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar" >
                <div className="flex items-center justify-between mb-3 px-2">
                  <span className="text-xs font-bold uppercase text-cc-text-subtle tracking-wider">Structure</span>
                  <button onClick={() => addSection(null)} className="p-1 hover:bg-cc-tile-hover rounded text-cc-accent"><Plus size={14} /></button>
                </div>
                <div className="space-y-0.5">
                  <button
                    onClick={() => setActiveSectionId('WHOLE_DOCUMENT')}
                    className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 mb-2 ${activeSectionId === 'WHOLE_DOCUMENT'
                      ? 'bg-cc-accent text-cc-accent-text'
                      : 'text-cc-text hover:bg-cc-tile-hover'
                      }`}
                  >
                    <BookOpen size={14} />
                    Whole Document
                  </button>
                  {tempIdea.sections?.filter(s => !s.parentId).sort((a, b) => a.order - b.order).map(section => renderSectionItem(section))}
                </div>
              </div >

              {/* Attachments Section */}
              < div className="p-4 border-t border-cc-border bg-cc-elevated relative" >
                {showImagePrompt && (
                  <div className="absolute bottom-full left-0 right-0 p-4 bg-cc-elevated shadow-2xl border-t border-cc-border animate-in slide-in-from-bottom-5 z-20 gpu-layer">
                    <div className="flex justify-between items-center mb-2">
                      <h5 className="text-xs font-bold text-cc-text">Generate Concept Art</h5>
                      <button onClick={() => setShowImagePrompt(false)}><X size={14} className="text-cc-text-muted" /></button>
                    </div>
                    <textarea
                      autoFocus
                      value={imagePrompt}
                      onChange={(e) => setImagePrompt(e.target.value)}
                      className="w-full bg-cc-surface rounded-lg p-2 text-xs border border-cc-border resize-none outline-none focus:ring-1 focus:ring-cc-accent text-cc-text"
                      placeholder="Describe the image (e.g., A futuristic dashboard UI...)"
                      rows={3}
                    />
                    <button
                      onClick={handleGenerateArt}
                      disabled={isGeneratingImage || !imagePrompt.trim()}
                      className="mt-2 w-full py-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-md hover:shadow-lg transition-all"
                    >
                      {isGeneratingImage ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                      Generate Art
                    </button>
                  </div>
                )}

                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-bold uppercase text-cc-text-subtle tracking-wider">Attachments</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setShowImagePrompt(!showImagePrompt)}
                      className="p-1 hover:bg-cc-accent-muted rounded text-cc-accent"
                      title="Generate AI Art"
                    >
                      <Palette size={14} />
                    </button>
                    <label className="cursor-pointer p-1 hover:bg-cc-tile-hover rounded text-cc-accent">
                      <Plus size={14} />
                      <input type="file" className="hidden" onChange={handleFileUpload} accept="image/*,.pdf,.txt" />
                    </label>
                  </div>
                </div>

                <div className="space-y-2 max-h-48 overflow-y-auto custom-scrollbar">
                  {tempIdea.attachments?.map(att => {
                    const sectionName = att.sectionId ? tempIdea.sections?.find(s => s.id === att.sectionId)?.title : null;
                    return (
                      <a key={att.id} href={getAttachmentLink(att)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-2 rounded-lg border border-cc-border-muted bg-cc-surface text-xs hover:bg-cc-accent-muted transition-colors group">
                        {att.type === 'image' ? (
                          <div className="w-8 h-8 rounded bg-cc-tile-hover bg-cover bg-center shrink-0" style={{ backgroundImage: att.data ? `url(${att.data})` : undefined }}>
                            {!att.data && <ImageIcon size={12} className="m-2 text-cc-text-muted" />}
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded bg-cc-accent-muted flex items-center justify-center text-cc-accent shrink-0"><FileText size={14} /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-cc-text truncate">{att.name}</p>
                          <p className="text-cc-text-muted text-[10px] flex items-center gap-1">
                            {sectionName && <span className="text-cc-accent">{sectionName}</span>}
                            {sectionName && <span>•</span>}
                            {new Date(att.timestamp).toLocaleDateString()}
                            {att.driveWebLink && <ExternalLink size={8} className="ml-1" />}
                          </p>
                        </div>
                        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); deleteAttachment(att.id); }} className="text-cc-text-muted hover:text-red-500 p-1 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12} /></button>
                      </a>
                    );
                  })}
                  {(!tempIdea.attachments || tempIdea.attachments.length === 0) && <div className="text-center py-4 border border-dashed border-cc-border rounded-lg"><Cloud size={20} className="mx-auto text-cc-text-subtle mb-1" /><p className="text-[10px] text-cc-text-muted">No files attached</p></div>}
                </div>
              </div >
            </div >

            {/* Hover Preview Panel (shows when sidebar is collapsed and hovered on desktop) */}
            {
              isSidebarCollapsed && isHoverPreviewVisible && (
                <div
                  className="absolute left-0 top-0 bottom-0 w-80 bg-cc-surface border-r border-cc-border shadow-2xl z-40 animate-in slide-in-from-left-5 duration-150 flex flex-col"
                  onMouseEnter={() => setIsHoverPreviewVisible(true)}
                  onMouseLeave={handleSidebarMouseLeave}
                >
                  {/* Sections Preview */}
                  <div className="flex-1 overflow-y-auto p-4 space-y-1 custom-scrollbar">
                    <div className="flex items-center justify-between mb-3 px-2">
                      <span className="text-xs font-bold uppercase text-cc-text-subtle tracking-wider">Structure</span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => addSection(null)} className="p-1 hover:bg-cc-tile-hover rounded text-cc-accent"><Plus size={14} /></button>
                        <button
                          onClick={() => { setIsSidebarCollapsed(false); setIsHoverPreviewVisible(false); }}
                          className="p-1 hover:bg-cc-tile-hover rounded text-cc-text-muted"
                          title="Pin sidebar open"
                        >
                          <PanelLeft size={14} />
                        </button>
                      </div>
                    </div>
                    <div className="space-y-0.5">
                      <button
                        onClick={() => setActiveSectionId('WHOLE_DOCUMENT')}
                        className={`w-full text-left px-2 py-1.5 rounded-lg text-xs font-bold transition-colors flex items-center gap-2 mb-2 ${activeSectionId === 'WHOLE_DOCUMENT'
                          ? 'bg-cc-accent text-cc-accent-text'
                          : 'text-cc-text hover:bg-cc-tile-hover'
                          }`}
                      >
                        <FileText size={14} />
                        Whole Document
                      </button>
                      {tempIdea.sections?.filter(s => !s.parentId).sort((a, b) => a.order - b.order).map(section => renderSectionItem(section))}
                    </div>
                  </div>

                  {/* Attachments Preview */}
                  <div className="p-4 border-t border-cc-border bg-cc-elevated">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold uppercase text-cc-text-subtle tracking-wider">Attachments</span>
                      <span className="text-xs text-cc-text-muted">{tempIdea.attachments?.length || 0}</span>
                    </div>
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {tempIdea.attachments?.slice(0, 3).map(att => (
                        <div key={att.id} className="flex items-center gap-2 p-1.5 rounded bg-cc-tile text-xs truncate">
                          {att.type === 'image' ? <div className="w-5 h-5 rounded bg-cover bg-center shrink-0" style={{ backgroundImage: `url(${att.data})` }} /> : <FileText size={12} className="text-cc-text-muted shrink-0" />}
                          <span className="truncate text-cc-text-muted">{att.name}</span>
                        </div>
                      ))}
                      {(!tempIdea.attachments || tempIdea.attachments.length === 0) && (
                        <p className="text-[10px] text-cc-text-muted text-center py-2">No attachments</p>
                      )}
                    </div>
                  </div>
                </div>
              )
            }

            {/* Mobile Tap-to-Expand Sheet (overlay on mobile when tapping the expand button) */}
            {
              isSidebarCollapsed && (
                <button
                  onClick={() => setIsSidebarCollapsed(false)}
                  className="md:hidden absolute left-2 top-1/2 -translate-y-1/2 z-30 p-3 bg-cc-accent rounded-full shadow-lg"
                  title="Open Organizer"
                >
                  <PanelLeft size={18} className="text-cc-accent-text" />
                </button>
              )
            }

            {/* Right Main: Rich Text Editor */}
            <div className="flex-1 flex flex-col bg-cc-elevated relative min-w-0 print-only">
              {/* PRINT ONLY HEADER */}
              <div className="hidden print-only mb-8">
                <h1>{tempIdea.title}</h1>
                <p>Generated by ConceptCanvas AI</p>
              </div>

              {activeSectionId ? (
                <div className="flex-1 p-8 overflow-y-auto flex flex-col print-only custom-scrollbar relative">
                  {getSectionsToRender().map(sec => (
                    <div key={sec.id} className="performance-container">
                      <SectionBlock
                        section={sec}
                        onUpdate={updateSectionContent}
                        theme={theme}
                        isActive={activeSectionId}
                        isWholeDoc={activeSectionId === 'WHOLE_DOCUMENT'}
                      />

                      {/* Section Attachments (Subset View) */}
                      {getSectionAttachments(sec.id).length > 0 && (
                        <div className="mt-4 pt-4 border-t border-cc-border-muted print-hide">
                          <div className="flex items-center gap-2 mb-2 text-xs font-bold uppercase text-cc-text-subtle tracking-wider">
                            <Paperclip size={12} /> Section Attachments
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {getSectionAttachments(sec.id).map(att => (
                              <a
                                key={att.id}
                                href={getAttachmentLink(att)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-cc-border-muted bg-cc-surface text-xs hover:bg-cc-accent-muted transition-colors"
                              >
                                <FileText size={14} className="text-cc-accent" />
                                <span className="font-medium text-cc-text truncate max-w-[120px]">{att.name}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Add new section prompt if Whole Document View */}
                  {activeSectionId === 'WHOLE_DOCUMENT' && (
                    <div className="mt-8 pt-8 border-t-2 border-dashed border-cc-border text-center">
                      <button onClick={() => addSection(null)} className="px-4 py-2 bg-cc-surface hover:bg-cc-tile-hover border border-cc-border rounded-lg text-sm font-medium text-cc-text transition-colors">
                        + Add New Section
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-400 print-hide"><FolderPlus size={48} className="mb-4 opacity-20" /><p>Select a section from the left to edit content</p></div>
              )}
            </div>

            {/* Chat Side Panel (Right) - Split View */}
            {chatProps && isChatPanelOpen && (
              <>
                {/* Resize Handle */}
                <div
                  className="w-1 bg-cc-border hover:bg-cc-accent cursor-col-resize flex-shrink-0 transition-colors z-30"
                  onMouseDown={startChatResize}
                />
                <div
                  ref={chatPanelRef}
                  style={{ width: chatPanelWidth }}
                  className="border-l border-cc-border bg-cc-base flex-shrink-0 animate-in slide-in-from-right-10 duration-200 shadow-xl z-20 flex flex-col overflow-hidden"
                >
                  <ChatInterface
                    {...chatProps}
                    activeIdea={tempIdea || chatProps.activeIdea}
                    hideHeader={true}
                    isChatCollapsed={false} // Force open
                    setIsChatCollapsed={undefined}
                    showControlsBar={true}
                    showCompactSearch={true}
                  />
                </div>
              </>
            )}

          </div > {/* End Main Layout (Flex Row) */}

          {/* Export Selection Modal */}
          {tempIdea && selectedExportType && (
            <ExportSelectionModal
              isOpen={exportModalOpen}
              onClose={() => {
                setExportModalOpen(false);
                setSelectedExportType(null);
              }}
              sections={tempIdea.sections || []}
              projectTitle={tempIdea.title}
              exportType={selectedExportType}
              onExport={handleExportWithSections}
              isExporting={isGeneratingPPT || isGeneratingXLS}
            />
          )}

          {/* Import Modal */}
          <ImportProjectModal
            isOpen={importModalOpen}
            onClose={() => setImportModalOpen(false)}
            onImport={handleImport}
            existingTitle={editingId ? tempIdea?.title : undefined}
          />
        </div >
      )}
    </div >
  );
});